/**
 * OrderStatusStore
 * 
 * 
 */

import type { OrderStatusModel } from "@/models/OrderStatusModel";
import type { Ref } from "vue";

import { defineStore } from "pinia";
import { ref } from "vue";
import Api from "@/services/ApiService";
import Cache from "@/services/Cache";
import Events from "@/services/Emitter";

const orderStatusStore = () =>
{
    const CACHE_KEY = "store_orderStatus_data";
    const list = ref<Record<number, OrderStatusModel>>({});
    const isInitialized = ref<boolean>(false);
    let initPromise: Promise<void> | null = null;

    const isEmpty = ():boolean =>
    {
        return Object.keys(list.value).length === 0;
    }

    const init = async (): Promise<void> => {
        // If already initialized, return immediately
        if (isInitialized.value) return;
        
        // If initialization is in progress, return the existing promise
        if (initPromise) return initPromise;
        
        // Start new initialization
        initPromise = (async () => {
            try {
                // Check cache first
                const cached = await Cache.get(CACHE_KEY);
                if (cached && typeof cached === 'object' && Object.keys(cached).length > 0) {
                    list.value = cached;
                    isInitialized.value = true;
                    return;
                }
                
                // Load from server if cache is empty
                await reload();
            } finally {
                // Clear the promise when done (success or failure)
                initPromise = null;
            }
        })();
        
        return initPromise;
    };
    
    const reload = async (runInBackground:boolean = false)
        :Promise<Ref<Record<number, OrderStatusModel>>> =>
    {
        let response = await Api.loadOrderStatus(runInBackground);
        
        // Extract data array from ApiResponse
        let tmp = response.Data || [];
        let rec = {} as Record<number, OrderStatusModel>;
        
        for (let i:number = 0; i < tmp.length; i++)
        {
            if (tmp[i].Id) {
                rec[tmp[i].Id!] = tmp[i];
            }
        }
        
        list.value = rec;
        await Cache.set(CACHE_KEY, list.value);
        isInitialized.value = true;
        return list;
    }

    const refresh = async (): Promise<void> => {
        await reload();
    };

    const load = async (runInBackground:boolean = false)
        :Promise<Ref<Record<number, OrderStatusModel>>> =>
    {
        await init();
        return list;
    }
    
    const getById = async (status:number):Promise<OrderStatusModel | null> =>
    {
        await init();
        
        if (list.value[status]) {
            return list.value[status];
        }
        
        return null;
    }

    // register global events
    Events.on("OrderStatusStore::Reload", async () =>
    {
        console.debug("OrderStatusStore::Reload called...");
        await reload();
    });

    Events.on("refresh:orderStatusStore", async () => {
        console.debug("refresh:orderStatusStore event received");
        await refresh();
    });

    // Auto-initialize on store creation
    init();

    return { load, reload, refresh, getById, list };
};

export const useOrderStatusStore = defineStore("orderStatus", orderStatusStore);